import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Alert, Modal, Segmented, Slider, Space, Spin, Typography } from 'antd';
import { EMAIL_BANNER } from '@crm/shared';
import { palette } from '../../app/theme';

type Mode = 'banner' | 'original';

interface Props {
  file: File | null;
  onCancel: () => void;
  /** the cropped and compressed picture, ready to upload */
  onDone: (file: File) => void;
  confirmLoading?: boolean;
}

const MAX_WIDTH = EMAIL_BANNER.width;
const kb = (n: number) => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

/**
 * Crops a picture to the 1200×500 email banner (drag it up or down to choose what shows) or keeps its shape, and
 * compresses it, showing the result and its size before anything is uploaded.
 */
export function ImageCropper({ file, onCancel, onDone, confirmLoading }: Props) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [mode, setMode] = useState<Mode>('banner');
  const [quality, setQuality] = useState(80);
  // where the frame sits on the picture: 0 = top/left edge, 1 = bottom/right edge
  const [pos, setPos] = useState({ x: 0.5, y: 0.5 });
  const [result, setResult] = useState<{ blob: Blob; url: string; width: number; height: number } | null>(null);
  const [encoding, setEncoding] = useState(false);
  const frame = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; pos: { x: number; y: number } } | null>(null);

  // load the chosen file
  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const el = new Image();
    el.onload = () => setImg(el);
    el.onerror = () => setFailed(true);
    el.src = url;
    setSrc(url);
    setImg(null);
    setFailed(false);
    setPos({ x: 0.5, y: 0.5 });
    setMode('banner');
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const png = file?.type === 'image/png' && mode === 'original';
  const layout = img ? cover(img, mode) : null;

  // encode the result (debounced) so the preview and size match what will be uploaded
  useEffect(() => {
    if (!img || !layout) return;
    let cancelled = false;
    setEncoding(true);
    const t = window.setTimeout(async () => {
      const blob = await render(img, layout, pos, png ? null : quality / 100);
      if (cancelled || !blob) return;
      setResult((prev) => {
        if (prev) URL.revokeObjectURL(prev.url);
        return { blob, url: URL.createObjectURL(blob), width: layout.outW, height: layout.outH };
      });
      setEncoding(false);
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [img, mode, quality, pos.x, pos.y, png]);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (mode !== 'banner') return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, pos };
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current || !layout || !frame.current) return;
    const rect = frame.current.getBoundingClientRect();
    const scale = rect.width / layout.outW; // screen px per output px
    const slackX = (layout.drawW - layout.outW) * scale;
    const slackY = (layout.drawH - layout.outH) * scale;
    const start = drag.current;
    setPos({
      x: slackX > 0 ? clamp(start.pos.x - (e.clientX - start.x) / slackX) : 0.5,
      y: slackY > 0 ? clamp(start.pos.y - (e.clientY - start.y) / slackY) : 0.5,
    });
  };
  const endDrag = () => (drag.current = null);
  const nudge = (dy: number) => setPos((p) => ({ ...p, y: clamp(p.y + dy) }));

  const canMoveY = !!layout && layout.drawH - layout.outH > 1;
  const canMoveX = !!layout && layout.drawW - layout.outW > 1;
  const small = !!img && img.naturalWidth < MAX_WIDTH;

  return (
    <Modal
      title="Crop and compress picture"
      open={!!file}
      onCancel={onCancel}
      width={780}
      okText="Use picture"
      okButtonProps={{ disabled: !result || encoding }}
      confirmLoading={confirmLoading}
      onOk={() => result && file && onDone(new File([result.blob], `${file.name.replace(/\.[^.]+$/, '') || 'picture'}.${png ? 'png' : 'jpg'}`, { type: result.blob.type }))}
      destroyOnHidden
    >
      {failed ? (
        <Alert type="error" showIcon title="This file could not be read as a picture. Try a JPG or PNG." />
      ) : !img || !layout || !src ? (
        <div style={{ display: 'grid', placeItems: 'center', height: 240 }}><Spin /></div>
      ) : (
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <Segmented
            value={mode}
            onChange={(v) => setMode(v as Mode)}
            options={[
              { value: 'banner', label: `Banner ${EMAIL_BANNER.width} × ${EMAIL_BANNER.height}` },
              { value: 'original', label: 'Keep its shape' },
            ]}
          />
          <div
            ref={frame}
            role={mode === 'banner' ? 'slider' : undefined}
            aria-label={mode === 'banner' ? 'Picture position' : undefined}
            aria-valuemin={mode === 'banner' ? 0 : undefined}
            aria-valuemax={mode === 'banner' ? 100 : undefined}
            aria-valuenow={mode === 'banner' ? Math.round(pos.y * 100) : undefined}
            tabIndex={mode === 'banner' ? 0 : undefined}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp') (e.preventDefault(), nudge(-0.05));
              if (e.key === 'ArrowDown') (e.preventDefault(), nudge(0.05));
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            style={{
              position: 'relative',
              width: '100%',
              aspectRatio: `${layout.outW} / ${layout.outH}`,
              maxHeight: mode === 'original' ? 420 : undefined,
              overflow: 'hidden',
              borderRadius: 8,
              background: palette.canvas,
              cursor: mode === 'banner' && (canMoveX || canMoveY) ? (drag.current ? 'grabbing' : 'grab') : 'default',
              touchAction: 'none',
              userSelect: 'none',
              outline: `1px solid ${palette.borderStrong}`,
            }}
          >
            <img
              src={src}
              alt=""
              draggable={false}
              style={{
                position: 'absolute',
                width: `${(layout.drawW / layout.outW) * 100}%`,
                height: `${(layout.drawH / layout.outH) * 100}%`,
                left: `${(-(layout.drawW - layout.outW) * pos.x / layout.outW) * 100}%`,
                top: `${(-(layout.drawH - layout.outH) * pos.y / layout.outH) * 100}%`,
                maxWidth: 'none',
                pointerEvents: 'none',
              }}
            />
          </div>
          {mode === 'banner' && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {canMoveY ? 'Drag the picture up or down (or use the arrow keys) to choose what shows in the banner.' : canMoveX ? 'Drag the picture left or right to choose what shows in the banner.' : 'The picture fits the banner exactly.'}
            </Typography.Text>
          )}
          {small && <Alert type="warning" showIcon title={`This picture is ${img.naturalWidth}px wide, less than ${MAX_WIDTH}px, so it may look soft when stretched.`} />}

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 220px', gap: 20, alignItems: 'start' }}>
            <div>
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                {png ? 'Quality (PNG keeps every detail and transparency, so it is not compressed further)' : `Quality · ${quality}%`}
              </Typography.Text>
              <Slider min={40} max={95} step={5} value={quality} onChange={setQuality} disabled={png} marks={{ 40: 'Smaller', 95: 'Sharper' }} />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Most inboxes load pictures under 200 KB quickly. 75–85% is a good balance.</Typography.Text>
            </div>
            <div>
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Result</Typography.Text>
              <div style={{ position: 'relative', border: `1px solid ${palette.border}`, borderRadius: 6, overflow: 'hidden', background: palette.canvas }}>
                {result && <img src={result.url} alt="Compressed result" style={{ display: 'block', width: '100%' }} />}
                {encoding && <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'rgba(255,255,255,.5)' }}><Spin size="small" /></div>}
              </div>
              {result && (
                <Typography.Text style={{ display: 'block', fontSize: 12, marginTop: 4, color: result.blob.size > 400 * 1024 ? palette.warning : palette.textSecondary }}>
                  {result.width} × {result.height}px · {kb(result.blob.size)}{file ? ` (was ${kb(file.size)})` : ''}
                </Typography.Text>
              )}
            </div>
          </div>
        </Space>
      )}
    </Modal>
  );
}

interface Layout {
  outW: number;
  outH: number;
  drawW: number;
  drawH: number;
}

/** Output size and how big the picture is drawn into it: covering the banner, or scaled down to 1200 wide. */
function cover(img: HTMLImageElement, mode: Mode): Layout {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (mode === 'banner') {
    const scale = Math.max(EMAIL_BANNER.width / w, EMAIL_BANNER.height / h);
    return { outW: EMAIL_BANNER.width, outH: EMAIL_BANNER.height, drawW: w * scale, drawH: h * scale };
  }
  const scale = Math.min(1, MAX_WIDTH / w);
  const outW = Math.round(w * scale);
  const outH = Math.round(h * scale);
  return { outW, outH, drawW: outW, drawH: outH };
}

function render(img: HTMLImageElement, l: Layout, pos: { x: number; y: number }, quality: number | null): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  canvas.width = l.outW;
  canvas.height = l.outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.resolve(null);
  ctx.imageSmoothingQuality = 'high';
  if (quality !== null) {
    // JPEG has no transparency: put see-through parts on white, like an email background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, l.outW, l.outH);
  }
  ctx.drawImage(img, -(l.drawW - l.outW) * pos.x, -(l.drawH - l.outH) * pos.y, l.drawW, l.drawH);
  return new Promise((resolve) => canvas.toBlob(resolve, quality === null ? 'image/png' : 'image/jpeg', quality ?? undefined));
}

const clamp = (n: number) => Math.min(1, Math.max(0, n));
